// src/views/HalalVSHaram.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import styles from './HalalVSHaram.module.css';
import useGameLogic from "../hooks/useGameLogic";
import LoadingSpinner from "../components/LoadingSpinner";
import FeedbackModal from '../components/FeedbackModal';
import GamePopups from "../components/GamePopups";
import commonGameStyles from './GameCommon.module.css';
import { shuffleArray } from "../utils/helpers";
import { usePreloadedData } from "../context/PreloadContext";

// --- Feedback Modal Constants ---
const FEEDBACK_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSfCYQQ3NJS3nRLoVB9Q1LtELQxQizAD37t1LMwJ_5-ei2dryA/viewform?usp=dialog";
const FEEDBACK_PROMPT_INTERVAL = 3;
const FEEDBACK_PROMPT_DISMISS_DAYS = 7;

// Local Storage Keys
const LS_KEY_GAMES_PLAYED = 'hvh_games_played_count';
const LS_KEY_LAST_PROMPT_TIME = 'hvh_last_prompt_time';
const LS_KEY_DISMISSED_PERMANENTLY = 'hvh_dismissed_permanently';
const LS_KEY_HVH_FIRST_COMPLETED = 'hvh_first_completed';

function HalalVSHaram({ inJourney = false, onComplete, gameLength = 10 }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const autoAdvanceTimeoutRef = useRef(null);
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;

    const [selectedOption, setSelectedOption] = useState(null);
    const [score, setScore] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const [timer, setTimer] = useState(30);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showInstructions, setShowInstructions] = useState(true);
    const [shuffledOptions, setShuffledOptions] = useState([]);
    const [done, setDone] = useState(false); // Game completion state

    const ACTIVITY_TYPE = "halalvsharam";
    const { preloadedData } = usePreloadedData();

    // Use centralized game logic hook
    const {
        lives,
        showGameOverPopup,
        showStreakPopup,
        streakMessage,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
        handleCorrectAttempt,
        handleIncorrectAttempt,
        resetGameLogicStates,
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
        isGameEnded, // ⬅️ IMPORT isGameEnded from the hook
    } = useGameLogic({
        activityType: ACTIVITY_TYPE,
        initialLives: 5,
        gameSpecificEndCondition: done, // Hook uses this to know the game is completed
        currentScore: score,
        gameLength,
        inJourney, // Pass prop to hook
        onComplete, // Pass prop to hook for journey progression
        feedbackConfig: {
            FEEDBACK_FORM_URL,
            FEEDBACK_PROMPT_INTERVAL,
            FEEDBACK_PROMPT_DISMISS_DAYS,
            LS_KEY_GAMES_PLAYED,
            LS_KEY_LAST_PROMPT_TIME,
            LS_KEY_DISMISSED_PERMANENTLY,
            LS_KEY_FIRST_COMPLETED: LS_KEY_HVH_FIRST_COMPLETED,
        },
        navigate,
    });

    const normalizeString = (str) => (typeof str === 'string' ? str.trim().toLowerCase() : '');

    // --- Data Loading and Initialization ---
    useEffect(() => {
        if (!user) return;
        resetGameLogicStates();
        // Use optional chaining in case preloadedData is not yet populated.
        if (preloadedData?.halalvsharam) {
            loadQuizQuestions(preloadedData.halalvsharam);
        } else {
            loadQuizQuestions();
        }
    }, [user, loadQuizQuestions, resetGameLogicStates, preloadedData]);

    // --- Shuffle options when question changes ---
    useEffect(() => {
        if (questions.length > 0 && current < questions.length) {
            setShuffledOptions(shuffleArray(questions[current].options));
        }
    }, [questions, current]);

    // --- Game Flow Handlers ---

    // Start quiz
    const startQuiz = useCallback(() => {
        setShowInstructions(false);
        setTimer(30);
    }, []);

    // Next question/End game
    const handleAdvance = useCallback(() => {
        setSelectedOption(null);
        setShowExplanation(false);
        setTimer(30);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent(prev => prev + 1);
        } else {
            setDone(true); // Signal completion
            // The useGameLogic hook will now immediately call 'onComplete' if inJourney is true.
        }
    }, [current, questions.length, setCurrent]);

    // Timeout logic
    const handleTimeout = useCallback(() => {
        setSelectedOption(null);
        setShowExplanation(true);
        setIsCorrect(false);
        handleIncorrectAttempt(questions[current]?._id);
    }, [handleIncorrectAttempt, questions, current]);

    // Select option
    const handleSelect = useCallback((option) => {
        if (showGameOverPopup) return;
        setSelectedOption(option);
    }, [showGameOverPopup]);

    // Submit answer
    const handleSubmit = useCallback(async () => {
        if (selectedOption === null || showGameOverPopup) return;
        setShowExplanation(true);

        const currentQuestion = questions[current];
        if (!currentQuestion) return;

        const isCorrectAnswer = normalizeString(selectedOption) === normalizeString(currentQuestion.correctAnswerText);
        setIsCorrect(isCorrectAnswer);

        if (isCorrectAnswer) {
            setScore(prev => prev + 1);
            handleCorrectAttempt(currentQuestion._id);
        } else {
            handleIncorrectAttempt(currentQuestion._id);
        }

        // Save progress (skip guest)
        if (!user || !user.uid || user.uid === 'guest-user') return;

        try {
            await axios.post(`${backendApiUrl}/user-progress`, {
                questionId: currentQuestion._id,
                activityType: ACTIVITY_TYPE,
                answeredCorrectly: isCorrectAnswer,
            }, { headers: { 'X-User-Id': user.uid } });
        } catch (err) {
            console.error('HalalVSHaram: User progress POST failed:', err.message);
        }
    }, [
        selectedOption,
        current,
        questions,
        user,
        ACTIVITY_TYPE,
        backendApiUrl,
        showGameOverPopup,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    ]);

    // --- Effects ---

    // Timer effect
    useEffect(() => {
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions ||
            showGameOverPopup
        ) return;

        const interval = setInterval(() => {
            setTimer(prev => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [
        current,
        showExplanation,
        done,
        loadingQuestions,
        questions.length,
        showInstructions,
        handleTimeout,
        showGameOverPopup,
    ]);

    // Auto advance after explanation
    useEffect(() => {
        if (!showExplanation || showGameOverPopup) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            handleAdvance();
        }, 8000);
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, handleAdvance, showGameOverPopup]);

    // --- Utility Function ---

    // Styling helper for options
    const getOptionClass = (option) => {
        if (!questions || questions.length === 0 || current >= questions.length) {
            return styles.optionButton;
        }

        const normSel = normalizeString(selectedOption);
        const normOpt = normalizeString(option);
        const normAns = normalizeString(questions[current].correctAnswerText);

        if (showExplanation) {
            if (normOpt === normAns) return `${styles.optionButton} ${styles.correct}`;
            if (normOpt === normSel) return `${styles.optionButton} ${styles.incorrect}`;
            return styles.optionButton;
        }
        return normOpt === normSel ? `${styles.optionButton} ${styles.selected}` : styles.optionButton;
    };

    // --- Render Logic ---
    const backgroundStyle = { backgroundImage: `url("/images/halal.jpg")` };

    if (!user)
        return (
            <p className={styles.message}>Please login to access the Halal vs Haram quiz.</p>
        );


    if (inJourney && isGameEnded) {
        return null; // Renders nothing immediately, allowing JourneyPage to unmount this component cleanly.
    }

    if (loadingQuestions || error || questions.length === 0) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.quizContainer}>
                    {loadingQuestions && <LoadingSpinner />}
                    {error && (
                        <>
                            <p className={styles.errorMessage}>Error: {error}</p>
                            <button onClick={loadQuizQuestions} className={styles.proceedButton}>
                                Retry Loading Questions
                            </button>
                        </>
                    )}
                    {questions.length === 0 && !loadingQuestions && (
                        <p className={styles.message}>
                            No questions found. Please seed the database or check backend API.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const currentQuestion = questions[current];
    const progressPercent = ((current + 1) / questions.length) * 100;

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.quizContainer}>
                <h2 className={styles.title}>Halal or Haram Quiz</h2>

                {/* --- Instructions --- */}
                {showInstructions ? (
                    <div className={styles.instructionsPopup}>
                        <h3>How to Play:</h3>
                        <ul>
                            <li>You will be presented with scenarios or items.</li>
                            <li>Decide if it is 'Halal' (permissible) or 'Haram' (forbidden).</li>
                            <li>Your score will be recorded on the leaderboard!</li>
                        </ul>
                        <button className={styles.proceedButton} onClick={startQuiz}>
                            Proceed
                        </button>
                    </div>
                ) : done ? (
                    // We removed the 'inJourney ? null : (...)' block here because the early return handles it.
                    // This block now only handles the STANDALONE result screen.
                    <div className={styles.scoreSection}>
                        {showGameOverPopup ? (
                            <>
                                <h3 className={commonGameStyles.gameOverTitle}>
                                    Game Over! 💔
                                </h3>
                                <p className={commonGameStyles.finalScore}>
                                    Your score: {score} / {questions.length}
                                </p>
                            </>
                        ) : (
                            <>
                                <h2>Your Score: {score} / {questions.length}</h2>
                                <p>Well done! 🎉</p>
                            </>
                        )}
                        <button
                            onClick={() => {
                                resetGameLogicStates();
                                loadQuizQuestions();
                            }}
                            className={styles.proceedButton}
                        >
                            Play Again
                        </button>
                        <button
                            onClick={() => navigate('/home')}
                            className={styles.proceedButton}
                            style={{ marginLeft: '10px' }}
                        >
                            Back to Home
                        </button>
                    </div>
                ) : (
                    <>
                        {/* --- Main Quiz UI --- */}
                        <p className={styles.progressText}>
                            Question {current + 1} of {questions.length}
                        </p>
                        <div className={styles.progressWrapper}>
                            <div
                                className={styles.progressBar}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <div className={commonGameStyles.livesContainer}>
                            {[...Array(5)].map((_, i) => (
                                <span
                                    key={i}
                                    className={`${commonGameStyles.heartIcon} ${
                                        i < lives
                                            ? commonGameStyles.filledHeart
                                            : commonGameStyles.emptyHeart
                                    }`}
                                >
                  ❤️
                </span>
                            ))}
                        </div>

                        {!showExplanation && <div className={styles.timer}>⏱ {timer}s</div>}

                        <h3 className={styles.question}>{currentQuestion.question}</h3>

                        <ul className={styles.options}>
                            {shuffledOptions.map((option, i) => (
                                <li key={i}>
                                    <button
                                        className={getOptionClass(option)}
                                        onClick={() => handleSelect(option)}
                                        disabled={showExplanation || showGameOverPopup}
                                    >
                                        {option}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {!showExplanation && (
                            <button
                                className={styles.submit}
                                onClick={handleSubmit}
                                disabled={selectedOption === null}
                            >
                                Submit
                            </button>
                        )}

                        {/* --- Explanation --- */}
                        {showExplanation && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                                    padding: "2rem",
                                    borderRadius: "1rem",
                                    width: "90%",
                                    maxWidth: "600px",
                                    textAlign: "center",
                                    color: "#eee",
                                    zIndex: 1000,
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: "1.2rem",
                                        fontWeight: "bold",
                                        color: isCorrect ? "green" : "red",
                                    }}
                                >
                                    {isCorrect ? '✅ Correct, MashaAllah!' : '❌ Incorrect.'}
                                </p>
                                <p>
                                    <strong>Explanation:</strong> {currentQuestion.explanation}
                                </p>
                                <p style={{ fontSize: "0.9rem", color: "#ccc" }}>
                                    <em>{currentQuestion.reference}</em>
                                </p>
                                <button
                                    className={styles.nextButton}
                                    onClick={handleAdvance}
                                    disabled={showGameOverPopup}
                                >
                                    {current + 1 < questions.length
                                        ? 'Next Question'
                                        : 'View Results'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* --- Popups and Feedback --- */}
            {!inJourney && ( // Only show popups/feedback in standalone mode
                <>
                    <GamePopups
                        showStreakPopup={showStreakPopup}
                        streakMessage={streakMessage}
                        showEncouragementPopup={showEncouragementPopup}
                        encouragementMessage={encouragementMessage}
                    />
                    <FeedbackModal
                        isOpen={showFeedbackModal}
                        onClose={handleCloseFeedbackModal}
                        onDismissPermanently={handleDismissFeedbackPermanently}
                        formUrl={FEEDBACK_FORM_URL}
                        gameName="Halal vs Haram"
                    />
                </>
            )}
        </div>
    );
}

export default HalalVSHaram;

